/**
 * Resolved user-service API base (includes `/api`, no trailing slash).
 *
 * **Targets:** `browser` (Local machine user-service), `dev`, `prod` — URLs from
 * `VITE_USER_SERVICE_URL_BROWSER`, `VITE_USER_SERVICE_URL_DEV`, `VITE_USER_SERVICE_URL_PROD` only
 * (no `VITE_USER_SERVICE_URL`).
 *
 * **Storybook:** `localStorage` key `usp_api_target` selects a target. If unset, defaults with
 * `pickDefaultUserServicePlaygroundTarget()` (`VITE_STORYBOOK_DEFAULT_TARGET` or Local).
 *
 * **Main app:** no `usp_api_target` → same URL as the **Local** target (`_BROWSER` env or default).
 */

const STORAGE_KEY_TARGET = "usp_api_target";
const LEGACY_STORAGE_KEY_URL = "usp_api_base_url";

export type UserServiceTarget = "browser" | "dev" | "prod";

export type UserServiceTargetOption = {
  id: UserServiceTarget;
  label: string;
  url: string;
};

function normalizeBase(raw: string): string {
  return raw.replace(/\/+$/, "");
}

function envString(key: string): string | undefined {
  const v = import.meta.env[key as keyof ImportMetaEnv] as string | undefined;
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

/** Accept legacy `VITE_USER_SERVICE_URL_LOCAL` name as alias for `_BROWSER`. */
export function getUserServiceBrowserUrl(): string {
  return normalizeBase(
    envString("VITE_USER_SERVICE_URL_BROWSER") ??
      envString("VITE_USER_SERVICE_URL_LOCAL") ??
      "http://localhost:3002/api",
  );
}

export function getUserServiceDevUrl(): string | undefined {
  const v = envString("VITE_USER_SERVICE_URL_DEV");
  return v ? normalizeBase(v) : undefined;
}

export function getUserServiceProdUrl(): string | undefined {
  const v = envString("VITE_USER_SERVICE_URL_PROD");
  return v ? normalizeBase(v) : undefined;
}

export function getUserServiceTargetOptions(): UserServiceTargetOption[] {
  const out: UserServiceTargetOption[] = [
    { id: "browser", label: "Local", url: getUserServiceBrowserUrl() },
  ];
  const dev = getUserServiceDevUrl();
  if (dev) out.push({ id: "dev", label: "Dev", url: dev });
  const prod = getUserServiceProdUrl();
  if (prod) out.push({ id: "prod", label: "Prod", url: prod });
  return out;
}

/** True when at least one remote (non-browser) URL is configured. */
export function hasUserServiceRemoteEnvUrls(): boolean {
  return Boolean(getUserServiceDevUrl() || getUserServiceProdUrl());
}

/** Storybook-only hint when Dev/Prod env URLs are missing. */
export function getUserServiceEnvConfigHint(): string | null {
  if (hasUserServiceRemoteEnvUrls()) return null;
  return "Set VITE_USER_SERVICE_URL_DEV and/or VITE_USER_SERVICE_URL_PROD in .env to enable Dev and Prod targets.";
}

function migrateLegacyStorage(): void {
  try {
    const rawTarget = localStorage.getItem(STORAGE_KEY_TARGET);
    if (rawTarget === "local") {
      localStorage.setItem(STORAGE_KEY_TARGET, "browser");
    }
    if (localStorage.getItem(STORAGE_KEY_TARGET)) return;
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY_URL);
    if (!legacy?.trim()) return;
    const url = normalizeBase(legacy.trim());
    const opt = getUserServiceTargetOptions().find((o) => o.url === url);
    if (opt) localStorage.setItem(STORAGE_KEY_TARGET, opt.id);
    localStorage.removeItem(LEGACY_STORAGE_KEY_URL);
  } catch {
    // ignore
  }
}

export function pickDefaultUserServicePlaygroundTarget(): UserServiceTarget {
  const opts = getUserServiceTargetOptions();
  const ids = new Set(opts.map((o) => o.id));

  const explicit = envString("VITE_STORYBOOK_DEFAULT_TARGET")?.toLowerCase();
  if (explicit === "dev" && ids.has("dev")) return "dev";
  if (explicit === "prod" && ids.has("prod")) return "prod";
  if (explicit === "browser" && ids.has("browser")) return "browser";

  return "browser";
}

export function getUserServicePlaygroundTarget(): UserServiceTarget {
  migrateLegacyStorage();
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TARGET) as UserServiceTarget | "local" | null;
    const normalized =
      raw === "local" ? "browser" : raw === "browser" || raw === "dev" || raw === "prod" ? raw : null;
    if (normalized) {
      const allowed = new Set(getUserServiceTargetOptions().map((o) => o.id));
      if (allowed.has(normalized)) return normalized;
    }
  } catch {
    // ignore
  }
  return pickDefaultUserServicePlaygroundTarget();
}

export function setUserServicePlaygroundTarget(target: UserServiceTarget): void {
  try {
    localStorage.setItem(STORAGE_KEY_TARGET, target);
  } catch {
    // ignore
  }
}

/** Clear stored target; next read uses `pickDefaultUserServicePlaygroundTarget()`. */
export function clearUserServicePlaygroundTarget(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_TARGET);
    localStorage.removeItem(LEGACY_STORAGE_KEY_URL);
  } catch {
    // ignore
  }
}

export function getUserServiceApiBase(): string {
  migrateLegacyStorage();
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TARGET) as UserServiceTarget | "local" | null;
    const normalized =
      raw === "local" ? "browser" : raw === "browser" || raw === "dev" || raw === "prod" ? raw : null;
    if (normalized) {
      const hit = getUserServiceTargetOptions().find((o) => o.id === normalized);
      if (hit) return hit.url;
    }
  } catch {
    // ignore
  }

  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY_URL);
    if (legacy?.trim()) return normalizeBase(legacy.trim());
  } catch {
    // ignore
  }

  const opts = getUserServiceTargetOptions();
  const def = pickDefaultUserServicePlaygroundTarget();
  const hit = opts.find((o) => o.id === def);
  return hit?.url ?? getUserServiceBrowserUrl();
}
